import type { Temporal } from '@insio/dates';
import { ApprovalStatus } from './approval-status';

/**
 * Purchase Order Status
 *
 * The legacy lifecycle status of a purchase order (nákupní objednávka), derived from its
 * `ApprovalStatus` and `dispatchedAt`; never stored. History and the AI chat speak it.
 */
export const PurchaseOrderStatus = {
  /** Being prepared (Rozpracovaná) — approval UNPROCESSED */
  DRAFT: 'DRAFT',
  /** Under approval (Ke schválení) — approval PENDING or FOR_REVIEW */
  FOR_APPROVAL: 'FOR_APPROVAL',
  /** No longer written: a partly decided round is still FOR_REVIEW (Předschváleno) */
  PRE_APPROVED: 'PRE_APPROVED',
  /** Approved, not yet sent (Schváleno) */
  APPROVED: 'APPROVED',
  /** Approved and sent to the supplier — `dispatchedAt` set (Odesláno dodavateli) */
  DISPATCHED: 'DISPATCHED',
  /** Refused (Neschváleno) */
  REJECTED: 'REJECTED'
} as const;

export type PurchaseOrderStatus = (typeof PurchaseOrderStatus)[keyof typeof PurchaseOrderStatus];

/** An order as the dispatch flag reads it. */
export interface DispatchableOrder {
  readonly dispatchedAt?: Temporal.Instant | null;
}

/** Whether the order was sent to the supplier; only an approved order is ever sent. */
export const isPurchaseOrderDispatched = (order: DispatchableOrder): boolean =>
  order.dispatchedAt != null;

/** The legacy status an order's approval status and dispatch stamp are written and shown as. */
export const purchaseOrderStatusOf = (
  approvalStatus: ApprovalStatus,
  order: DispatchableOrder
): PurchaseOrderStatus => {
  switch (approvalStatus) {
    case ApprovalStatus.UNPROCESSED:
      return PurchaseOrderStatus.DRAFT;
    case ApprovalStatus.PENDING:
    case ApprovalStatus.FOR_REVIEW:
      return PurchaseOrderStatus.FOR_APPROVAL;
    case ApprovalStatus.APPROVED:
      return isPurchaseOrderDispatched(order)
        ? PurchaseOrderStatus.DISPATCHED
        : PurchaseOrderStatus.APPROVED;
    case ApprovalStatus.REJECTED:
      return PurchaseOrderStatus.REJECTED;
  }
};

/**
 * The approval status a move to a legacy target status lands in; under approval is PENDING,
 * a partial approval FOR_REVIEW. DISPATCHED is no approval move and has none.
 */
export const purchaseOrderApprovalTargetOf = (
  status: Exclude<PurchaseOrderStatus, typeof PurchaseOrderStatus.DISPATCHED>
): ApprovalStatus => {
  switch (status) {
    case PurchaseOrderStatus.DRAFT:
      return ApprovalStatus.UNPROCESSED;
    case PurchaseOrderStatus.FOR_APPROVAL:
      return ApprovalStatus.PENDING;
    case PurchaseOrderStatus.PRE_APPROVED:
      return ApprovalStatus.FOR_REVIEW;
    case PurchaseOrderStatus.APPROVED:
      return ApprovalStatus.APPROVED;
    case PurchaseOrderStatus.REJECTED:
      return ApprovalStatus.REJECTED;
  }
};

/**
 * Allowed approval moves. A move absent from this map is rejected — the state machine is
 * enforced, never assigned blindly. Sending an approved order is `dispatchedAt`, not a move.
 */
export const PURCHASE_ORDER_STATUS_TRANSITIONS: Record<ApprovalStatus, readonly ApprovalStatus[]> =
  {
    UNPROCESSED: [ApprovalStatus.PENDING],
    // Back to UNPROCESSED withdraws the open approval round.
    PENDING: [
      ApprovalStatus.FOR_REVIEW,
      ApprovalStatus.APPROVED,
      ApprovalStatus.REJECTED,
      ApprovalStatus.UNPROCESSED
    ],
    FOR_REVIEW: [ApprovalStatus.APPROVED, ApprovalStatus.REJECTED, ApprovalStatus.UNPROCESSED],
    APPROVED: [],
    REJECTED: [ApprovalStatus.UNPROCESSED]
  };

/** Whether the approval move is one the lifecycle allows. */
export const isPurchaseOrderStatusTransitionAllowed = (
  from: ApprovalStatus,
  to: ApprovalStatus
): boolean => PURCHASE_ORDER_STATUS_TRANSITIONS[from].includes(to);

/**
 * Whether a purchase order may still be edited: approval not started; a rejected one goes back
 * first. Approval is the point of no return — a later change is a revision, not an edit.
 */
export const isPurchaseOrderEditable = (approvalStatus: ApprovalStatus): boolean =>
  approvalStatus === ApprovalStatus.UNPROCESSED;

/** Whether a purchase order with this approval status may be taken back for editing. */
export const canReturnPurchaseOrderToDraft = (approvalStatus: ApprovalStatus): boolean =>
  isPurchaseOrderStatusTransitionAllowed(approvalStatus, ApprovalStatus.UNPROCESSED);

/** Whether the order's approval is running or waiting for a user. */
export const isPurchaseOrderUnderApproval = (approvalStatus: ApprovalStatus): boolean =>
  approvalStatus === ApprovalStatus.PENDING || approvalStatus === ApprovalStatus.FOR_REVIEW;

/**
 * Orders goods may be received against: approved, sent or not. Early and phoned-in deliveries are
 * normal, and refusing them would force an unsolicited receipt, losing the order link.
 */
export const RECEIVABLE_PURCHASE_ORDER_STATUSES: readonly ApprovalStatus[] = [
  ApprovalStatus.APPROVED
];

/** Whether a delivery can be recorded against an order with this approval status. */
export const isPurchaseOrderReceivable = (approvalStatus: ApprovalStatus): boolean =>
  RECEIVABLE_PURCHASE_ORDER_STATUSES.includes(approvalStatus);

/** Whether the order may be sent to the supplier: approved and not sent yet. */
export const isPurchaseOrderDispatchable = (
  order: DispatchableOrder & { readonly approvalStatus: ApprovalStatus }
): boolean => order.approvalStatus === ApprovalStatus.APPROVED && !isPurchaseOrderDispatched(order);

/** Orders held for a decision — the automation's or a person's. */
export const PURCHASE_ORDER_AWAITING_DECISION_STATUSES: readonly ApprovalStatus[] = [
  ApprovalStatus.PENDING,
  ApprovalStatus.FOR_REVIEW
];

/** Submitted and not yet finished. `REJECTED` is excluded — it is returned, not in flight. */
export const PURCHASE_ORDER_IN_FLIGHT_STATUSES: readonly ApprovalStatus[] = [
  ApprovalStatus.PENDING,
  ApprovalStatus.FOR_REVIEW,
  ApprovalStatus.APPROVED
];

/**
 * Approval statuses a purchase order may be cancelled from (`cancelledAt`, not a status) — before
 * a decision or after a rejection; an approved one may have receipts and invoices behind it.
 */
export const PURCHASE_ORDER_CANCELLABLE_STATUSES: readonly ApprovalStatus[] = [
  ApprovalStatus.UNPROCESSED,
  ApprovalStatus.REJECTED
];

/** Whether a purchase order with this approval status may be cancelled. */
export const isPurchaseOrderCancellable = (approvalStatus: ApprovalStatus): boolean =>
  PURCHASE_ORDER_CANCELLABLE_STATUSES.includes(approvalStatus);
